import { useEffect, useId, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Icon } from '@/components/ui'
import { cn } from '@/lib/cn'
import type { Option } from './vocabulary'
import styles from './fields.module.css'

interface ComboboxProps {
  id: string
  /** The committed value. Empty string means nothing chosen. */
  value: string
  options: Option[]
  /** Whether a value outside `options` may be committed. */
  allowNew?: boolean
  placeholder?: string
  /** Draws a glyph beside an option — what an icon name actually looks like. */
  glyph?: (value: string) => ReactNode
  onCommit: (next: string) => void
  /** Backspace on an empty box, which chip inputs use to remove the last one. */
  onBackspaceEmpty?: () => void
}

/** Below this, a search box is more clutter than help. */
const SEARCH_THRESHOLD = 7

/**
 * A picker, built from the site's own materials.
 *
 * ## Pick first, type only to create
 *
 * The control is a button showing what is currently chosen. Clicking it opens
 * the list. There is no typing involved in choosing something that already
 * exists — which was the complaint about the first version of this: it put
 * the cursor in an empty text box, so assigning an existing domain felt like
 * being asked to remember and spell it.
 *
 * A search box appears inside the panel only when the list is long enough to
 * need one, and for an open vocabulary it doubles as the way to name
 * something new: a query matching nothing offers itself as a new entry.
 *
 * ## Why not `<select>`
 *
 * Its popup is drawn by the operating system, not the page. No border radius,
 * no surface colour, no typeface shared with anything around it, and no way
 * to show a glyph beside the name of a glyph.
 *
 * What is kept from it is the behaviour: arrows move, Enter takes, Escape
 * abandons, the active row scrolls itself into view, and nothing is committed
 * by drifting past it — so tabbing through a form cannot silently change a
 * value.
 */
export const Combobox = ({
  id,
  value,
  options,
  allowNew = false,
  placeholder = 'Choose',
  glyph,
  onCommit,
  onBackspaceEmpty,
}: ComboboxProps) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  const wrap = useRef<HTMLDivElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const search = useRef<HTMLInputElement>(null)
  const listId = useId()

  const selected = options.find((option) => option.value === value)
  /* The label when one is known, the raw value when it is not — a value that
     resolves to nothing still has to be visible, or it cannot be corrected. */
  const display = selected?.label ?? value

  const needle = query.trim().toLowerCase()
  const matches = needle
    ? options.filter(
        (option) =>
          option.label.toLowerCase().includes(needle) ||
          option.value.toLowerCase().includes(needle),
      )
    : options

  // An exact match is already on the list; offering to "create" it would be
  // the same row twice.
  const exact = options.some(
    (option) =>
      option.value.toLowerCase() === needle ||
      option.label.toLowerCase() === needle,
  )
  const creating = allowNew && needle !== '' && !exact
  const rows = creating
    ? [{ value: query.trim(), label: query.trim() }, ...matches]
    : matches

  // A search box for a long list, and always for one that can be added to —
  // there is nowhere else to type a new entry.
  const searchable = allowNew || options.length >= SEARCH_THRESHOLD

  useEffect(() => {
    if (!open) return
    const onDown = (event: PointerEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])

  useEffect(() => {
    if (open && searchable) search.current?.focus()
  }, [open, searchable])

  // Keep the highlighted row on screen while arrowing through a long list —
  // forty-four icon names do not fit in the panel.
  useEffect(() => {
    if (!open) return
    list.current?.children[active]?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  const show = () => {
    setOpen(true)
    setQuery('')
    const index = options.findIndex((option) => option.value === value)
    setActive(index >= 0 ? index : 0)
  }

  const commit = (next: string) => {
    onCommit(next)
    setOpen(false)
    setQuery('')
    setActive(0)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        show()
        return
      }
      const step = event.key === 'ArrowDown' ? 1 : -1
      setActive((i) =>
        rows.length === 0 ? 0 : (i + step + rows.length) % rows.length,
      )
      return
    }

    if (event.key === 'Enter' || (event.key === ' ' && !open)) {
      if (!open) {
        event.preventDefault()
        show()
        return
      }
      event.preventDefault()
      const row = rows[active]
      if (row) commit(row.value)
      return
    }

    if (event.key === 'Escape' && open) {
      event.preventDefault()
      setOpen(false)
      setQuery('')
      return
    }

    if (event.key === 'Backspace' && query === '' && onBackspaceEmpty) {
      onBackspaceEmpty()
    }
  }

  return (
    <div className={styles.combo} ref={wrap}>
      {/*
        * A button, not a text input. Nothing here is typed unless something
        * new is being named, and a cursor blinking in an empty box says the
        * opposite.
        */}
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        className={cn(styles.comboField, open && styles.comboFieldOpen)}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKeyDown}
      >
        {glyph && value ? (
          <span className={styles.comboGlyph} aria-hidden="true">
            {glyph(value)}
          </span>
        ) : null}

        <span
          className={cn(styles.comboValue, !display && styles.comboPlaceholder)}
        >
          {display || placeholder}
        </span>

        <span className={styles.comboCaret} aria-hidden="true">
          <Icon name={open ? 'caretUp' : 'caretDown'} size={12} />
        </span>
      </button>

      {open ? (
        <div className={styles.comboPanel}>
          {searchable ? (
            <input
              ref={search}
              type="text"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setActive(0)
              }}
              onKeyDown={onKeyDown}
              placeholder={allowNew ? 'Search, or type a new one' : 'Search'}
              className={styles.comboSearch}
              autoComplete="off"
              spellCheck={false}
              aria-controls={listId}
              aria-activedescendant={
                rows[active] ? `${listId}-${active}` : undefined
              }
            />
          ) : null}

          <ul id={listId} role="listbox" ref={list} className={styles.comboList}>
            {rows.length === 0 ? (
              <li className={styles.comboEmpty}>
                {allowNew ? 'Type to add a new one' : 'No match'}
              </li>
            ) : (
              rows.map((row, index) => (
                <li
                  key={`${row.value}-${index}`}
                  id={`${listId}-${index}`}
                  role="option"
                  aria-selected={row.value === value}
                  className={cn(
                    styles.comboOption,
                    index === active && styles.comboOptionActive,
                    row.value === value && styles.comboOptionSelected,
                  )}
                  onMouseEnter={() => setActive(index)}
                  /* Takes the pointer before the search box loses focus, so a
                     click on an option is not cancelled by the blur. */
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => commit(row.value)}
                >
                  {glyph ? (
                    <span className={styles.comboOptionGlyph} aria-hidden="true">
                      {glyph(row.value)}
                    </span>
                  ) : null}

                  <span className={styles.comboOptionLabel}>{row.label}</span>

                  {creating && index === 0 ? (
                    <span className={styles.comboBadge}>new</span>
                  ) : row.label !== row.value ? (
                    <span className={styles.comboOptionValue}>{row.value}</span>
                  ) : null}
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
